/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import { useEffect, useMemo, useState } from 'react';
import Dashboard, { DashboardRow, DashboardSection, DashboardSectionTitle } from '@g17eco/molecules/dashboard';
import { WorkInProgress } from '@g17eco/molecules/work-in-progress';
import { ErrorComponent } from '@features/error';
import { useRouteMatch } from 'react-router-dom';
import { DATE, formatDate } from '@utils/date';
import { getPeriodName, DataPeriods } from '@utils/dataPeriods';
import { AdminBreadcrumbs } from '@routes/admin-dashboard/AdminBreadcrumbs';
import { naturalSort } from '@utils/index';
import {
  ALL_CHECK_TYPES,
  ALL_STATUSES,
  DATA_QUALITY_CHECK_LABELS,
  DataQualityFilters,
  DataQualityIssueType,
  StatusFilter,
  useCreateDataQualityJobMutation,
  useGetDataQualityJobResultsQuery,
  useGetLatestDataQualityJobQuery,
} from '@api/data-quality';
import G17Client from '@services/G17Client';
import { JobStatus } from '@g17eco/types/background-jobs';
import { isProcessingJob } from '@utils/background-job';
import { skipToken } from '@reduxjs/toolkit/query';
import { BasicAlert } from '@g17eco/molecules/alert';
import { toastFromError } from '@g17eco/molecules/toasts';
import { useAppSelector } from '@reducers/index';
import { getCurrentInitiativeTree } from '@selectors/initiative';
import {
  FilterState,
  ResultsFilterState,
  DEFAULT_FILTERS,
  DEFAULT_RESULTS_FILTERS,
  POLL_INTERVAL,
} from './constants';
import { DataQualityFiltersSection } from './DataQualityFiltersSection';
import { DataQualityResultsSection } from './DataQualityResultsSection';
import { GroupedIssue } from './DataQualityResultsTable';

const downloadCsv = (initiativeId: string, jobId: string): Promise<void> =>
  G17Client.downloadFile(`/initiatives/${initiativeId}/data-quality/jobs/${jobId}/results/csv`);

// Generate compact filter summary from filter state
const generateFilterSummary = (
  filterState: {
    years?: string[];
    months?: string[];
    periods?: DataPeriods[] | string[];
    initiativeIds?: string[];
    checkTypes?: DataQualityIssueType[];
  },
  totalInitiatives: number
): string => {
  const parts: string[] = [];

  // Years summary
  if (filterState.years && filterState.years.length > 0) {
    const sortedYears = [...filterState.years].sort();
    if (sortedYears.length <= 3) {
      parts.push(sortedYears.join(', '));
    } else {
      parts.push(`${sortedYears[0]}-${sortedYears[sortedYears.length - 1]}`);
    }
  }

  // Periods summary
  if (filterState.periods && filterState.periods.length > 0) {
    parts.push(filterState.periods.map((p) => getPeriodName(p as DataPeriods)).join(', '));
  }

  // Subsidiaries summary
  if (!filterState.initiativeIds || filterState.initiativeIds.length === 0) {
    parts.push('All subsidiaries');
  } else if (filterState.initiativeIds.length === totalInitiatives && totalInitiatives > 0) {
    parts.push('All subsidiaries');
  } else {
    parts.push(`${filterState.initiativeIds.length} subsidiaries`);
  }

  // Check types summary
  if (!filterState.checkTypes || filterState.checkTypes.length === ALL_CHECK_TYPES.length) {
    parts.push('All checks');
  } else if (filterState.checkTypes.length > 0) {
    parts.push(filterState.checkTypes.map((t) => DATA_QUALITY_CHECK_LABELS[t]).join(', '));
  }

  return parts.join(' • ');
};

export const DataQualityRoute = () => {
  const routeMatch = useRouteMatch<{ initiativeId: string }>();
  const initiativeId = routeMatch.params.initiativeId;

  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [downloading, setDownloading] = useState(false);
  const [hasInitializedSubsidiaries, setHasInitializedSubsidiaries] = useState(false);
  const [resultsFilters, setResultsFilters] = useState<ResultsFilterState>(DEFAULT_RESULTS_FILTERS);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Get initiative tree for subsidiaries filter
  const initiativeTree = useAppSelector(getCurrentInitiativeTree);
  const initiativeOptions = useMemo(() =>
    initiativeTree.map((init) => ({
      value: init._id,
      label: init.name,
      searchString: init.name,
    })),
    [initiativeTree]
  );

  // Set all subsidiaries as default when tree loads
  useEffect(() => {
    if (initiativeTree.length > 0 && !hasInitializedSubsidiaries) {
      setFilters((prev) => ({
        ...prev,
        initiativeIds: initiativeTree.map((init) => init._id),
      }));
      setHasInitializedSubsidiaries(true);
    }
  }, [initiativeTree, hasInitializedSubsidiaries]);

  // Track polling state - updated when job status changes
  const [isPolling, setIsPolling] = useState(false);

  // Get latest job status with conditional polling
  const {
    data: latestJob,
    isError: isJobError,
  } = useGetLatestDataQualityJobQuery(initiativeId ?? skipToken, {
    pollingInterval: isPolling ? POLL_INTERVAL : 0,
  });

  // Create job mutation
  const [createJob, { isLoading: isCreatingJob }] = useCreateDataQualityJobMutation();

  // Determine job state and update polling
  const isJobInProgress = latestJob ? isProcessingJob(latestJob) : false;

  // Sync polling state with job status
  useEffect(() => {
    setIsPolling(isJobInProgress);
  }, [isJobInProgress]);
  const isJobCompleted = latestJob?.status === JobStatus.Completed;
  const isJobFailed = latestJob?.status === JobStatus.Error;

  // Get results from completed job
  const {
    data: jobResults,
    isLoading: isLoadingResults,
    isError: isResultsError,
  } = useGetDataQualityJobResultsQuery(
    latestJob && isJobCompleted ? { initiativeId, jobId: latestJob._id } : skipToken
  );

  // Generate summary for last run filters
  const lastRunFilterSummary = useMemo(() => {
    if (!jobResults?.filters) {
      return undefined;
    }
    return generateFilterSummary(jobResults.filters, initiativeTree.length);
  }, [jobResults?.filters, initiativeTree.length]);

  // Extract unique filter options from results
  const resultsFilterOptions = useMemo(() => {
    const rawResults = jobResults?.results ?? [];
    const initiativeNames = [...new Set(rawResults.map((r) => r.initiativeName))].sort();
    const issueTypes = [...new Set(rawResults.map((r) => r.issueType))];
    const statuses = [...new Set(rawResults.map((r) => r.status))].sort();

    return {
      initiativeNames: initiativeNames.map((name) => ({
        value: name,
        label: name,
        searchString: name,
      })),
      issueTypes: issueTypes.map((type) => ({
        value: type,
        label: DATA_QUALITY_CHECK_LABELS[type] ?? type,
        searchString: DATA_QUALITY_CHECK_LABELS[type] ?? type,
      })),
      statuses: statuses.map((status) => ({
        value: status,
        label: status.charAt(0).toUpperCase() + status.slice(1),
        searchString: status,
      })),
    };
  }, [jobResults?.results]);

  // Filter and group results
  const { filteredResults, groupedResults } = useMemo(() => {
    const rawResults = jobResults?.results ?? [];

    // Apply results filters
    const filtered = rawResults.filter((issue) => {
      if (resultsFilters.initiativeNames.length > 0 && !resultsFilters.initiativeNames.includes(issue.initiativeName)) {
        return false;
      }
      if (resultsFilters.issueTypes.length > 0 && !resultsFilters.issueTypes.includes(issue.issueType)) {
        return false;
      }
      if (resultsFilters.statuses.length > 0 && !resultsFilters.statuses.includes(issue.status)) {
        return false;
      }
      return true;
    });

    // Group by UTRV
    const groupMap = new Map<string, GroupedIssue>();
    for (const issue of filtered) {
      const existing = groupMap.get(issue.utrvId);
      if (existing) {
        existing.issues.push(issue);
      } else {
        groupMap.set(issue.utrvId, {
          utrvId: issue.utrvId,
          initiativeName: issue.initiativeName,
          initiativeId: issue.initiativeId,
          surveyId: issue.surveyId,
          utrCode: issue.utrCode,
          utrName: issue.utrName,
          effectiveDate: issue.effectiveDate,
          status: issue.status,
          issues: [issue],
          isExpanded: expandedGroups.has(issue.utrvId),
        });
      }
    }

    return {
      filteredResults: filtered,
      groupedResults: Array.from(groupMap.values()).sort((a, b) => {
        const nameCompare = naturalSort(a.initiativeName, b.initiativeName);
        if (nameCompare !== 0) {
          return nameCompare;
        }
        return naturalSort(a.utrCode, b.utrCode);
      }),
    };
  }, [jobResults?.results, resultsFilters, expandedGroups]);

  // Total results count from raw data (before filtering)
  const totalResultsCount = jobResults?.totalIssues ?? 0;

  const handleFilterChange = (key: keyof FilterState, values: string[] | DataPeriods[] | DataQualityIssueType[] | StatusFilter[]) => {
    setFilters((prev) => ({ ...prev, [key]: values }));
  };

  const handleRunCheck = async () => {
    if (!initiativeId || filters.checkTypes.length === 0) {
      return;
    }

    const jobFilters: Partial<DataQualityFilters> = {
      checkTypes: filters.checkTypes,
      statuses: filters.statuses.length > 0 ? filters.statuses : ALL_STATUSES,
      initiativeIds: filters.initiativeIds.length > 0 ? filters.initiativeIds : undefined,
      years: filters.years.length > 0 ? filters.years : undefined,
      months: filters.months.length > 0 ? filters.months : undefined,
      periods: filters.periods.length > 0 ? filters.periods : undefined,
    };

    await createJob({ initiativeId, filters: jobFilters })
      .unwrap()
      .catch((error: unknown) => toastFromError({ error, title: 'Failed to run data quality checks' }));
  };

  const handleDownloadCsv = async () => {
    if (!initiativeId || !latestJob || filteredResults.length === 0) {
      return;
    }
    setDownloading(true);
    try {
      await downloadCsv(initiativeId, latestJob._id);
    } finally {
      setDownloading(false);
    }
  };

  const handleResultsFilterChange = (key: keyof ResultsFilterState, values: string[] | DataQualityIssueType[]) => {
    setResultsFilters((prev) => ({ ...prev, [key]: values }));
  };

  const toggleGroupExpansion = (utrvId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(utrvId)) {
        next.delete(utrvId);
      } else {
        next.add(utrvId);
      }
      return next;
    });
  };

  const toggleAllGroups = () => {
    if (expandedGroups.size === groupedResults.length) {
      setExpandedGroups(new Set());
    } else {
      setExpandedGroups(new Set(groupedResults.map((g) => g.utrvId)));
    }
  };

  const clearResultsFilters = () => {
    setResultsFilters(DEFAULT_RESULTS_FILTERS);
  };

  const hasActiveResultsFilters =
    resultsFilters.initiativeNames.length > 0 ||
    resultsFilters.issueTypes.length > 0 ||
    resultsFilters.statuses.length > 0;

  if (isJobError || isResultsError) {
    return <ErrorComponent />;
  }

  // Only show loading state for results fetch, not initial job check or job creation
  const isLoading = isLoadingResults;
  const lastRunDate = latestJob?.completedDate ? formatDate(latestJob.completedDate, DATE.HUMANIZE) : undefined;
  const lastRunDateFull = latestJob?.completedDate ? formatDate(latestJob.completedDate, DATE.AT_TIME) : undefined;

  return (
    <Dashboard className='w-100'>
      <DashboardRow>
        <AdminBreadcrumbs breadcrumbs={[{ label: 'Data quality' }]} initiativeId={initiativeId} />
      </DashboardRow>
      <DashboardSectionTitle title='Data Quality Checks' />
      <DashboardSection>
        <p className='text-muted mb-3'>
          Run automated checks to find data quality issues across all subsidiaries in the organization hierarchy.
        </p>

        {isJobInProgress || isCreatingJob ? (
          <WorkInProgress
            title={
              <div className='text-center text-ThemeTextMedium'>
                <span>Data quality checks are running across all subsidiaries.</span>
                <br />
                <span>You can leave this page and return later.</span>
                <br />
                <span>We will send you a notification when the check is complete.</span>
              </div>
            }
          />
        ) : (
          <>
            {isJobFailed && (
              <BasicAlert type='danger' className='mb-3'>
                The last data quality check failed. Please try running it again.
              </BasicAlert>
            )}

            <DataQualityFiltersSection
              filters={filters}
              initiativeOptions={initiativeOptions}
              isLoading={isLoading}
              onFilterChange={handleFilterChange}
              onRunCheck={handleRunCheck}
            />

            <DataQualityResultsSection
              isLoading={isLoading}
              hasJob={!!latestJob}
              hasResults={!!jobResults}
              totalResultsCount={totalResultsCount}
              truncated={
                jobResults?.truncated === true
                  ? { stored: jobResults.results.length, total: jobResults.totalIssues }
                  : undefined
              }
              filteredResultsCount={filteredResults.length}
              lastRunDate={lastRunDate}
              lastRunDateFull={lastRunDateFull}
              lastRunFilterSummary={lastRunFilterSummary}
              downloading={downloading}
              resultsFilters={resultsFilters}
              resultsFilterOptions={resultsFilterOptions}
              groupedResults={groupedResults}
              expandedGroups={expandedGroups}
              hasActiveResultsFilters={hasActiveResultsFilters}
              onDownloadCsv={handleDownloadCsv}
              onResultsFilterChange={handleResultsFilterChange}
              onClearResultsFilters={clearResultsFilters}
              onToggleGroup={toggleGroupExpansion}
              onToggleAllGroups={toggleAllGroups}
            />
          </>
        )}
      </DashboardSection>
    </Dashboard>
  );
};
